import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  AppShell,
  Burger,
  Group,
  Text,
  NavLink as MNavLink,
  ScrollArea,
  Stack,
  Avatar,
  Menu,
  Badge,
  Tooltip,
  UnstyledButton,
  Box,
  useMantineColorScheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconLayoutDashboard,
  IconCash,
  IconArrowsRightLeft,
  IconBuildingStore,
  IconWallet,
  IconKey,
  IconUsersGroup,
  IconHistory,
  IconLogout,
  IconShieldCheck,
  IconBook,
  IconBuildingBank,
  IconSun,
  IconMoon,
  IconDeviceDesktop,
  IconReportAnalytics,
  IconChartBar,
  IconCalendarDollar,
  IconSettings,
} from '@tabler/icons-react';
import { useSession, usePermissions } from '../auth/SessionContext';
import { isMerchantUser, isPlatform } from '../auth/permissions';
import { Logo } from './Logo';
import { useTranslation } from '../i18n/LocaleContext';
import { LocaleSwitcher } from './LocaleSwitcher';
import type { Profile } from '../api/types';
import type { TranslationKey } from '../i18n/translations';

type NavGate = (ctx: {
  profile: Profile;
  has: (k: string) => boolean;
  isSuperuser: boolean;
}) => boolean;

type NavItem = {
  to: string;
  /** Translation key looked up via `useTranslation().t(...)`. */
  labelKey: TranslationKey;
  icon: typeof IconLayoutDashboard;
  show: NavGate;
};

/**
 * Menu visibility rules. Each item is shown when the viewer either has the
 * required permission key OR matches the legacy scope predicate (so platform
 * users with no role still see what they used to see during the rollout).
 *
 * Backend is the source of truth — these gates only avoid surfacing pages the
 * user couldn't actually use. Hitting the URL directly still triggers a 403
 * from the server.
 */
function buildNav(): NavItem[] {
  // Ordering principle: the TOP holds everyday tools used by everyone /
  // merchants; going DOWN, items become progressively more internal-only /
  // platform-admin. Neighbours are functionally related, so the list reads as a
  // flow of groups: entry → money flow → its analytics → API integration →
  // account & access → platform administration (most internal, at the bottom).
  return [
    // — Entry —
    {
      to: '/dashboard',
      labelKey: 'nav.overview',
      icon: IconLayoutDashboard,
      show: ({ has }) => has('overview.access'),
    },

    // — Money flow (core transactions): cash in → cash out → resulting balance —
    {
      to: '/dashboard/deposits',
      labelKey: 'nav.deposits',
      icon: IconCash,
      show: ({ has }) => has('deposits.list.view'),
    },
    {
      to: '/dashboard/disbursements',
      labelKey: 'nav.disbursements',
      icon: IconArrowsRightLeft,
      show: ({ has }) => has('disbursements.list.view'),
    },
    {
      to: '/dashboard/wallet',
      labelKey: 'nav.wallet',
      icon: IconWallet,
      // Merchant users see their own wallet (wallet.view); internal users /
      // superusers see the platform-wide all-merchant aggregate
      // (wallet.internal.view). Superuser bypasses via has().
      show: ({ has, profile }) =>
        (isMerchantUser(profile) && has('wallet.view')) ||
        (isPlatform(profile) && has('wallet.internal.view')),
    },

    // — Reports (analytics of the money flow above): summary → detailed export
    //   by transaction date → the same export by settlement date —
    {
      to: '/dashboard/reports/summary',
      labelKey: 'nav.transactionSummary',
      icon: IconChartBar,
      // On-screen aggregated summary (by method / bank). Same view permission
      // as the file report; internal cross-merchant is gated inside the page.
      show: ({ has, isSuperuser }) =>
        isSuperuser || has('report.transaction.view'),
    },
    {
      to: '/dashboard/reports/transactions',
      labelKey: 'nav.transactionReports',
      icon: IconReportAnalytics,
      // Visible to anyone with the view permission. The internal-only "all
      // merchants" filter is gated separately inside the page.
      show: ({ has, isSuperuser }) =>
        isSuperuser || has('report.transaction.view'),
    },
    {
      to: '/dashboard/reports/settlement',
      labelKey: 'nav.settlementReports',
      icon: IconCalendarDollar,
      // Daily report files of SETTLED deposits, by settlement date (the wallet's
      // settlement mutations). Same pipeline, page and permissions as
      // Transaction Reports.
      show: ({ has, isSuperuser }) =>
        isSuperuser || has('report.transaction.view'),
    },

    // — API integration (merchant-facing developer tools): keys → reference —
    {
      to: '/dashboard/api-credentials',
      labelKey: 'nav.apiCredentials',
      icon: IconKey,
      show: ({ has, profile }) => isMerchantUser(profile) && has('account.credentials.view'),
    },
    {
      to: '/dashboard/api-docs',
      labelKey: 'nav.apiDocs',
      icon: IconBook,
      // Gated by `apidocs.view` (superuser bypasses via has()). The page itself
      // still self-filters the endpoint list by the viewer's other permissions;
      // this permission controls whether the page is reachable at all.
      show: ({ has }) => has('apidocs.view'),
    },

    // — Account & access: own security → org users → the roles they hold —
    {
      to: '/dashboard/security',
      labelKey: 'nav.security',
      icon: IconShieldCheck,
      // Every authenticated user can manage their own MFA.
      show: () => true,
    },
    {
      to: '/dashboard/users',
      labelKey: 'nav.users',
      icon: IconUsersGroup,
      show: ({ has, profile }) =>
        isMerchantUser(profile)
          ? has('users.view') || has('users.manage')
          : has('users.internal.view') || has('users.internal.manage'),
    },
    {
      to: '/dashboard/roles',
      labelKey: 'nav.roles',
      icon: IconShieldCheck,
      show: ({ has }) => has('roles.merchant.manage') || has('roles.internal.manage'),
    },

    // — Platform administration (internal-only; most internal at the bottom):
    //   merchant accounts → the acquirers they route through → audit oversight —
    {
      to: '/dashboard/merchants',
      labelKey: 'nav.merchants',
      icon: IconBuildingStore,
      show: ({ has, profile }) => isPlatform(profile) && has('merchants.list.view'),
    },
    {
      to: '/dashboard/acquirers',
      labelKey: 'nav.acquirers',
      icon: IconBuildingBank,
      // Internal-only acquirer (payment-provider) management. Superuser bypasses.
      show: ({ has }) => has('acquirers.manage'),
    },
    {
      to: '/dashboard/settings',
      labelKey: 'nav.settings',
      icon: IconSettings,
      // SUPERADMIN ONLY — platform-wide settings (settlement timing, fees,
      // payment methods, auth). Hidden from merchants and internal staff; the
      // backend enforces requireSuperuser regardless.
      show: ({ isSuperuser }) => isSuperuser,
    },
    {
      to: '/dashboard/audit-log',
      labelKey: 'nav.auditLog',
      icon: IconHistory,
      show: ({ has }) => has('audit.view'),
    },
  ];
}

export function Shell() {
  const [opened, setOpened] = useState(false);
  const { state, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const perms = usePermissions();
  if (state.status !== 'authenticated') {
    // RequireAuth should have intercepted, but keep the fallback safe.
    return null;
  }
  const profile = state.profile;
  const items = buildNav().filter((i) =>
    i.show({ profile, has: perms.has, isSuperuser: perms.isSuperuser }),
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'md', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={() => setOpened((o) => !o)} hiddenFrom="md" size="sm" />
            <UnstyledButton component={Link} to="/dashboard" style={{ textDecoration: 'none' }}>
              <Logo height={26} />
            </UnstyledButton>
            <Badge color={profile.isSuperuser ? 'red' : profile.userType === 'internal' ? 'blue' : 'brand'}>
              {profile.isSuperuser
                ? t('auth.superuser')
                : profile.userType === 'internal'
                  ? t('auth.internal')
                  : t('auth.merchant')}
            </Badge>
          </Group>

          <Group gap="sm">
            <LocaleSwitcher />
            <ColorSchemeToggle />
            <Menu position="bottom-end" withArrow shadow="md">
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Avatar color="brand" size={32} radius="xl">
                      {profile.accountName.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm" fw={500}>
                        {profile.accountName}
                      </Text>
                      <Text c="dimmed" size="xs">
                        {profile.email}
                      </Text>
                    </Box>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{profile.email}</Menu.Label>
                <Menu.Divider />
                <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={handleLogout}>
                  {t('auth.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2}>
            {items.map((item) => {
              const Icon = item.icon;
              const active =
                location.pathname === item.to ||
                (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
              return (
                <MNavLink
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  // `end` disables NavLink's default prefix matching. Without
                  // it, the Overview link (to="/dashboard") would also be
                  // active on every nested /dashboard/* route because React
                  // Router treats parent paths as matching by default. We
                  // already compute `active` ourselves below — supporting
                  // both exact (Overview) and prefix (Deposits → DepositDetail).
                  end
                  label={t(item.labelKey)}
                  active={active}
                  leftSection={<Icon size={18} />}
                  onClick={() => setOpened(false)}
                />
              );
            })}
          </Stack>
        </AppShell.Section>
        <AppShell.Section>
          <Text size="xs" c="dimmed" ta="center" py="xs">
            v1.1 · GMT+7
          </Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

/**
 * Three-state color scheme toggle: light · dark · auto (follow OS).
 *
 * Click cycles through the three modes; the icon reflects the *effective*
 * scheme so users in 'auto' mode see whether the OS resolved to dark or
 * light. Choice is persisted via Mantine's localStorageColorSchemeManager
 * (configured in main.tsx).
 */
function ColorSchemeToggle() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  // useComputedColorScheme resolves 'auto' to whichever the OS actually picked,
  // so the icon never lies about the rendered theme.
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const { t } = useTranslation();

  const nextScheme: 'light' | 'dark' | 'auto' =
    colorScheme === 'light' ? 'dark' : colorScheme === 'dark' ? 'auto' : 'light';

  const tooltipLabel = (() => {
    if (colorScheme === 'auto') return t('theme.autoUsing', { scheme: computed });
    if (colorScheme === 'dark') return t('theme.darkClickToAuto');
    return t('theme.lightClickToDark');
  })();

  const icon = (() => {
    if (colorScheme === 'auto') return <IconDeviceDesktop size={18} />;
    return computed === 'dark' ? <IconMoon size={18} /> : <IconSun size={18} />;
  })();

  return (
    <Tooltip label={tooltipLabel} withArrow>
      <ActionIcon
        variant="default"
        size="lg"
        radius="md"
        onClick={() => setColorScheme(nextScheme)}
        aria-label={t('theme.toggle')}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );
}
